<template>
  <div class="hours-table-wrap">
    <HourEditModal 
      v-if="selectedEvent" 
      v-model="eventCreationState" 
      :selectedEvent="selectedEvent" 
      @delete-event="onDelete" 
      @delete-recurrent-event="onDeleteRecurrentHour"
      @save-hour="onSubmit" 
      @save-recurrent-hour="onSubmitRecurrentHour" 
      @cancel="cancelEventCreation" />
    
    <ConfirmationDialog 
      ref="recurrent_hour_update_confirmation_modal"
      :title="recurrentHourUpdateConfirmationTitle"
      :body="recurrentHourUpdateConfirmationBody"
      :options="recurrentHourUpdateConfirmationOptions"
      @cancel="cancelConfirmationDialog"
      @confirmed="onRecurrentHourUpdateConfirmation" />
    
    <ConfirmationDialog 
      ref="recurrent_hour_delete_confirmation_modal"
      :title="recurrentHourDeleteConfirmationTitle"
      :body="recurrentHourDeleteConfirmationBody"
      :options="recurrentHourDeleteConfirmationOptions"
      @cancel="cancelConfirmationDialog"
      @confirmed="onRecurrentHourDeleteConfirmation" />
    
    <ContactModal v-if="openContactModal" @created="onContactCreated" @cancel="onModalCancel" :force-open="openContactModal" />
    <ProjectModal v-if="openProjectModal" @created="onProjectCreated" @cancel="onModalCancel" :force-open="openProjectModal" :contact-data="projectModalContactId" />
    <ProjectModalSimple v-if="openProjectModalSimple" @created="onProjectCreated" @cancel="onModalCancel" />
    <CopyHourModal ref="copy_hour_modal" @copyHour="onCopyHour" :hour="hourToCopy" />
    
    <FilterBar 
      cookieName="HourFilterOptionsVue" 
      :filter-bar-type="'hours'"
      :specialFilters="[ 'CalendarViewSelector', 'CalendarNavigation' ]"
      :secondaryFilters="[ 'UserFilter', 'HourStatusFilter' ]"
    >
      <template v-slot:right-bar>
        <HourTotalsContainer />
      </template>
    </FilterBar>

    <div class="hours-wrapper content--inner flex-nowrap" id="hours-wrapper">
      <div class="calendar-sidemenu">
        <HourCustomerSidemenu 
          :mutation-name="childMutation"
          @change="selectProjectCustomer"
          @select-customer="selectCustomer"
          @open-project-modal-mini="onOpenProjectModalMini"
          @open-project-modal="onOpenProjectModal"
          @open-contact-modal="onOpenContactModal"
        />
      </div>
      <div class="calendar-wrapper">
        <div class="calendar shadowed" :class="['calendar-' + filters.activeView, {loading: calLoading, hide: calendarHidden}]" :key="calIndex"
          @mouseenter="trackMouse()"
          @mouseleave="untrackMouse()"
        >
          <VueCal v-if="filters.activeView" id="vuecal" ref="vuecal"
            :disable-views="['years', 'day']"
            :snap-to-time="15"
            :timeCellHeight="timeCellHeight"
            :timeFormat="'HH'"
            :active-view.sync="filters.activeView"
            @view-change="onViewChange"
            :transitions="true"
            hide-title-bar
            hide-view-selector
            :on-event-click="onEventClick"
            :editable-events="{ title: false, drag: !isTouchDevice(), resize: !isTouchDevice(), delete: false, create: true }"
            :events="events"
            :dblclick-to-navigate="false"
            :drag-to-create-event="!isTouchDevice()"
            :cell-click-hold="!isTouchDevice()"
            :drag-to-create-threshold="30"
            :selected-date="selectedDate"
            :on-event-create="onEventCreate"
            @cell-click="cellClick"
            @event-drag-create="onEventDragCreate"
            @event-change="onChange"
            @event-drop="onDrop"
            @event-delete="onDelete"
            @event-mouse-enter="weeklyCircleVisible = false"
            @event-mouse-leave="weeklyCircleVisible = true"
          >
            <!-- CELL CONTENT -->
            <template v-slot:cell-content="{ cell, view }">
              <div v-if="view.id === 'month'" :class="{ focused: (cell.formattedDate === focusedDate) }">
                <span class="vuecal__cell-date">{{cell.content}}</span>
                <div class="month-cell-overlay" :tabindex="cell.content" @click="dayClicked(cell.startDate)" :class="{ visible: (cell.formattedDate === focusedDate) }">
                  <span class="month-actions" @click="monthCreateEvent(cell)">
                    <font-awesome-icon aria-hidden="true" icon="fa-solid fa-circle-plus" /> 
                  </span>
                </div>
                <div v-if="hoursPerDay[cell.formattedDate]" class="vuecal__cell-events-count gk-count" :class="[getAmountColor(hoursPerDay[cell.formattedDate])]">
                  {{ getDayHours(hoursPerDay[cell.formattedDate]) }}
                </div>
              </div>
            </template>
            
            <!-- HEADING -->
            <template v-slot:weekday-heading="{ heading, view }">
              <div v-if="view.id === 'week'" class="weekday-heading">
                <div class="label">{{ getHeadingWeekday(heading) }}</div>
                <div class="time">{{ getHoursWeekday(view.events, heading.date) }}</div>
              </div>
              <div v-else>
                {{ heading.label }}
              </div>
            </template>

            <!-- WEEK VIEW EVENT -->
            <template v-slot:event="{ event, view }">
              <div v-if="view==='week'" class="timeentry-content">
                
                <button 
                  id="copy-button"
                  class="timeentry-copy-btn" 
                  @click="openCopyModal(event)"
                  draggable="true">
                      <font-awesome-icon id="copy-button-icon" @mouseover="copyHover = true" @mouseleave="copyHover = false" :icon="copyIcon"/>
                </button>
                
                <div class="vuecal__event-description">{{ (event) ? event.description : '' }}</div>
                <div class="vuecal__event-name">{{ (event.project) ? event.project.name : $gettext('Unassigned') }}</div>
                <div class="vuecal__event-time">{{ getTotalEventTime(event.start, event.end) }}</div>
                <div class="vuecal__event-avatar">
                  <Avatar v-if="event.created_by" :key="event.created_by.id" :userId="event.created_by.id" />
                </div>
              </div>
            </template>
          </VueCal>
        </div>
        <HourListTable @updated="hourListUpdated" ref="hour_list_per_day" @row-clicked="selectEventForEdit" @new-event="selectEventForCreation" @unfocus-day="unfocusDay()" @copyHour="openCopyModal"
                       @delete-recurrent-event="onDeleteRecurrentHour"
                       @delete-event="onDelete"
                       :hour-list="events" :focus-date="focusedDate" />
      </div>
    </div>
    <div v-if="filters.activeView === 'week' && !isTouchDevice()" class="circle-wrap" ref="weekly_circle" :class="{ visible: weeklyCircleVisible }" :style="{ top: weeklycircleY + 'px', left: weeklycircleX + 'px' }">
      <span class="circle" />
      <div class="hint">{{ $gettext('click or drag to create hour') }}</div>
    </div>
  </div>
</template>
<script>
  import { mapGetters, mapMutations } from "vuex";

  import VueCal from 'vue-cal'
  import 'vue-cal/dist/drag-and-drop.js'
  import 'vue-cal/dist/vuecal.css'
  import moment from 'moment';
  import ConfirmationDialog from "@/components/ui/ConfirmationDialog";
  import FilterBar from "@/components/list_filters/FilterBar";
  import HourService from "@/store/services/hour.service.js";
  import HourEditModal from "@/components/hours/HourEditModal";
  import HourCustomerSidemenu from "@/components/hours/HourCustomerSidemenu";
  import HourListTable from "@/components/hours/HourListTable";
  import ContactModal from "@/components/contacts/ContactModal";
  import ProjectModal from "@/components/contacts/ProjectModal";
  import ProjectModalSimple from "@/components/contacts/ProjectModalSimple";
  import Avatar from "@/components/Avatar";
  import CopyHourModal from "@/components/hours/CopyHourModal";
  import HourTotalsContainer from "@/components/hours/HourTotalsContainer";
  import { debounce } from "@/utils/";

  export default {
    name: "HoursTable",
    components: { HourTotalsContainer, Avatar, ContactModal, ProjectModal, VueCal, FilterBar, HourEditModal, HourCustomerSidemenu, HourListTable, ProjectModalSimple, CopyHourModal, ConfirmationDialog },
    props: {
      refreshHours: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        calIndex: 0,
        hourList: [],
        totalHours: 0,
        isDragging: false,
        selectedEvent: null,
        eventCreationState: null,
        selectedDate: "",
        filters: {},
        events: [],
        hoursPerDay:[],
        timeCellHeight: 70,
        gotoDefault: 8,
        eventDefault: {
          title: 'New Event',
          class: 'new-event-class',
          source:'click',
          project: { id: 0, name: 'Placeholder name' },
          created_by: { display_name: '' }
        },
        projectList: false,
        startTime: 0,
        unsubscribe: null,
        calLoading: false,
        parentMutation: 'filterStore/SET_UPDATE_FILTER_HOURS',
        childMutation: 'hourStore/SET_REFRESH_TOTALS',
        openContactModal: false,
        openProjectModal: false,
        projectModalContactId: null,
        openProjectModalSimple: false,
        scrollTrigger: '',
        weeklycircleX: 0,
        weeklycircleY: 0,
        weeklyCircleVisible: false,
        focusedDate: null,
        updatingEvent: false,
        hourToCopy: null,
        copyHover: false,
        recurrentHourUpdateConfirmationTitle: this.$gettext("You are adjusting a recurring entry"),
        recurrentHourUpdateConfirmationBody: this.$gettext("Do you also want to change the entries planned after this one?"),
        recurrentHourUpdateConfirmationOptions: [
          {key: "one", text: this.$gettext("Change only this entry")},
          {key: "all", text: this.$gettext("Change this entry and all subsequent entries")}
        ],
        recurrentUpdateResult: null,
        recurrentHourDeleteConfirmationTitle: this.$gettext("You are deleting a recurring entry"),
        recurrentHourDeleteConfirmationBody: this.$gettext("Do you also want to delete the entries planned after this one?"),
        recurrentHourDeleteConfirmationOptions: [
          {key: "one", text: this.$gettext("Delete only this entry")},
          {key: "all", text: this.$gettext("Delete this entry and all subsequent entries")}
        ],
        recurrentDeleteResult: null
      }
    },
    computed: {
      ...mapGetters ({ 
        currentUser: 'main/getCurrentUser',
        getCompanyId: 'main/getCompanyId'
      }),
      calendarHidden() {
        return this.$store.getters['hourStore/getListView'] 
      },
      copyIcon() {
        if (this.copyHover)
          return "fa fa-clone";
        else
          return "fa-regular fa-clone";
      }
    },
    created () {

      this.debouncedCreateRefresh = debounce(time_entry => {
        this.refreshOnCreate(time_entry)
      }, 300);

      this.debouncedEditRefresh = debounce(time_entry => {
        this.refreshOnEdit(time_entry)
      }, 300);

      this.$store.commit("hourStore/SET_DO_SCROLL", true);
      this.unsubscribe = this.$store.subscribe((mutation, state) => {
        if (mutation.type === this.parentMutation) {

          this.filters = state["filterStore"].filter;

          if(!this.validateDates(this.filters.start_date, this.filters.end_date)) {
            this.setInitialDates()
            this.filters = this.$store.getters['filterStore/getFilter'];
          }

          if(!this.filters.activeView){
            this.setInitialDates()
            this.filters = this.$store.getters['filterStore/getFilter'];
            this.$store.commit("hourStore/SET_DO_SCROLL", true);
          }
          
          if (this.filters.navigation && this.$refs.vuecal) {
            this.focusedDate = null
            if(this.filters.navigation === 'next'){
              this.$refs.vuecal.next();
            }else if(this.filters.navigation === 'prev'){
              this.$refs.vuecal.previous();
            }else{
              this.$refs.vuecal.switchView('week', new Date());
            }
            if(this.filters.activeView === 'month') {
              this.selectedDate = this.$store.getters['filterStore/getFilter'].start_date;
              this.$store.commit("hourStore/SET_DO_SCROLL", false);
            } else {
              this.$store.commit("hourStore/SET_DO_SCROLL", true);
            }
            this.$store.commit("filterStore/SET_CALENDAR_NAVIGATION", null);
            return
          }
          if(!this.$refs.vuecal) { 
            this.selectedDate = this.filters.start_date;
          }
          if(!this.$refs.vuecal || (this.filters.activeView === this.$refs.vuecal.activeView)) {
            this.getActiveViewItems()
          }
        }
      });
    },
    beforeDestroy() {
      this.unsubscribe();
      window.removeEventListener('mousemove', this.weeklyCursorEventListener);
    },
    mounted(){
    },
    methods: {
      ...mapMutations({
        setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
      }),
      validateDates (start, end) {
        return (this.isValidDate(start) && this.isValidDate(end))
      },
      isValidDate (date) {
        return (new Date(date) !== 'Invalid Date') && !isNaN(new Date(date)) && !(date.indexOf('Y-') > -1);
      },
      setInitialDates () {
        var curr = new Date; // get current date
        var first = curr.getDate() - curr.getDay() + 1; // First day is the day of the month - the day of the week
        var start_date = new Date(curr.setDate(first)).format('Y-m-d');
        var end_date = new Date(curr.setDate(curr.getDate() + 6)).format('Y-m-d');
        this.$store.commit("filterStore/SET_CALENDAR_VIEW", 'week');
        this.$store.commit("filterStore/SET_START_END_DATE", { start_date: start_date, end_date: end_date });
      },
      unfocusDay () {
        this.focusedDate = null
        this.$refs.hour_list_per_day.getHourList();
      },
      cellClick (e) {
        if (e && !e.date) {
          this.$refs.vuecal.createEvent(e, 60, this.eventDefault)
          this.focusedDate = e.format('Y-m-d')
        }
      },
      trackMouse () {
        if(!this.isTouchDevice()) {
          this.weeklyCircleVisible = true
          window.addEventListener('mousemove', this.weeklyCursorEventListener);
        }
      },
      untrackMouse () {
        this.weeklyCircleVisible = false
        window.removeEventListener('mousemove', this.weeklyCursorEventListener);
      },
      weeklyCursorEventListener (e) {
        this.weeklycircleX = e.pageX + 16;
        this.weeklycircleY = e.pageY - 16;
      },
      getActiveViewItems () {
        if (this.filters.activeView === "month") {
          this.getHoursPerDay();
        } else {
          this.getHourList();
        }
      },
      dayClicked (start_date) {
        this.selectedDate = start_date.format('Y-m-d')
        this.$refs.hour_list_per_day.getHourList({ start_date: start_date });
      },
      getHourList () {
        let filters = { ...this.filters }
        filters.extended = "True";
        filters.ordering = 'date';

        if(this.$store.getters["filterStore/getFilter"].created_by) {
          filters.created_by = this.$store.getters["filterStore/getFilter"].created_by;
        }

        filters.company_profile = this.getCompanyId;
        if(!filters.project && this.$store.getters["filterStore/getCurrentCustomer"]) {
          filters.project__company = this.$store.getters["filterStore/getCurrentCustomer"]
        }
        HourService.list(filters).then((response) => {
          this.events = [];
          for (var i = 0; i < response.data.results.length; i++) {
            if (this.$store.getters["filterStore/getUnassigned"] || filters.status === 'unassigned') {
              if (!response.data.results[i].project) { // only unassigned hours
                let event = HourService.timeentry2Event(response.data.results[i])
                this.events.push(event)
              }
            } else {
              if (filters.status && filters.status !== 'all') {  // only assigned
                if (response.data.results[i].project) {
                  let event = HourService.timeentry2Event(response.data.results[i])
                  this.events.push(event)
                }
              } else {
                let event = HourService.timeentry2Event(response.data.results[i])
                this.events.push(event)
              }
            }
          }
          (response.data.next)
            ? this.getMoreHours(response.data.next)
            : this.scrollToHour()
        })

      },
      getMoreHours (next) {
        HourService.loadNextPage(next).then((response) => {
          for (var i = 0; i < response.data.results.length; i++) {
            let event = HourService.timeentry2Event(response.data.results[i]);
            this.events.push(event);
          }
          (response.data.next)
            ? this.getMoreHours(response.data.next)
            : this.scrollToHour()
        })
      },
      getHoursPerDay(){
        this.calLoading = true
        let filters = { ...this.filters }
        filters.company_profile = this.getCompanyId;

        if(!filters.project) {
          filters.project__company = this.$store.getters["filterStore/getCurrentCustomer"]
        }
        
        if (this.$store.getters["filterStore/getUnassigned"] || filters.status === 'unassigned') {
          filters.assignment_status = 'unassigned' // only unassigned 
        } else if (filters.status && filters.status !== 'all') { 
          filters.assignment_status = 'assigned' // only assigned
        } else {
          if(filters.project__company) {
            filters.assignment_status = 'assigned' // only assigned
          } else {
            filters.assignment_status = '' // all hours
          }
        }
        HourService.hoursPerDay(filters).then((response) => {
          this.calLoading = false
          this.hoursPerDay = response;

          const filterDate = (this.focusedDate) ? { start_date: new Date(this.selectedDate) } : false
          this.$refs.hour_list_per_day.getHourList(filterDate);
        });
      },
      onViewChange(e) {
        this.$store.commit("filterStore/SET_CALENDAR_VIEW", e.view);
        this.$store.commit("filterStore/SET_START_END_DATE", {start_date:e.startDate.format('Y-m-d'), end_date:e.endDate.format('Y-m-d')});
        
        if(e.view === "month"){
          this.getHoursPerDay();
        }
        if(e.view === "week"){
          this.getHourList();
        }
        this.refreshTotals()
      },
      onChange(e) {
        if (e.originalEvent === null || e.originalEvent.type === 'template') {   // it means it is creating and not editing
          return;
        }

        if (typeof e.event.recurrent_time_entry === "number" && !e.event.cancel_update) {
          if (this.recurrentUpdateResult === "processing") {  // event date change
            this.recurrentUpdateResult = e.event.recurrent_update_result;
          } else {  // event duration change
            let dateDiff = e.event.end - e.event.start;
            dateDiff = Math.round(dateDiff) / (1000 * 60 * 60);

            HourService.retrieveRecurrentHour(e.event.recurrent_time_entry).then((response) => {
              this.recurrentUpdateResult = response.data;
              this.recurrentUpdateResult.selected_hour = {
                id: e.event.id,
                newDate: e.event.start.toISOString(),
                oldDate: e.originalEvent.start.toISOString(),
                amount: dateDiff.toString(),
                company_profile: this.getCompanyId,
                created_by: e.event.created_by.id || this.currentUser.id
              };
              e.event.date = e.event.start;
              e.event.company_profile = this.getCompanyId;
              e.event.amount = dateDiff.toString();
              this.recurrentUpdateResult = HourService.prepareRecurrentHour(this.recurrentUpdateResult, e.event);
            });
          }

          this.$refs.recurrent_hour_update_confirmation_modal.open();
        } else {
          this.updateEvent(e.event);
        }
      },
      onDrop(e) {
        if (typeof e.event.recurrent_time_entry === "number" && this._isDifferentDate(e)) {
          e.event.start = e.originalEvent.start;
          e.event.end = e.originalEvent.end;
          e.event.cancel_update = true;
        } else if (typeof e.event.recurrent_time_entry === "number" && !this._isDifferentDate(e)) {
          this.recurrentUpdateResult = "processing";
          HourService.retrieveRecurrentHour(e.event.recurrent_time_entry).then((response) => {
            this.recurrentUpdateResult = response.data;
            this.recurrentUpdateResult.selected_hour = {
              id: e.event.id,
              newDate: e.newDate.toISOString(),
              oldDate: e.oldDate.toISOString(),
              amount: e.event.amount,
              company_profile: this.getCompanyId,
              created_by: e.event.created_by.id || this.currentUser.id
            };
            e.event.date = e.newDate;
            e.event.company_profile = this.getCompanyId;
            this.recurrentUpdateResult = HourService.prepareRecurrentHour(this.recurrentUpdateResult, e.event);
          });
        }
      },
      _isDifferentDate(e) {
        if (typeof e.event.recurrent_time_entry === "number") {
          let auxOriginalStart;
          let auxDestinationStart;

          if (typeof e.originalEvent.start === "string") {
            auxOriginalStart = new Date(e.originalEvent.start);
          } else {
            auxOriginalStart = e.originalEvent.start;
          }

          if (typeof e.event.start === "string") {
            auxDestinationStart = new Date(e.event.start);
          } else {
            auxDestinationStart = e.event.start;
          }

          return auxOriginalStart.getDay() != auxDestinationStart.getDay();
        }
      },
      onEventClick(event, e) {
        if (e.target.id != "copy-button" && e.target.id != "copy-button-icon") {
          this.selectEventForEdit(event);
        
          // Prevent navigating to narrower view (default vue-cal behavior).
          e.stopPropagation();
        }
      },
      selectEventForEdit (event) {
        this.selectedEvent = event;
        this.eventCreationState = 'edit';
      },
      selectEventForCreation () {
        let selDate = false
        if(this.focusedDate) {
          selDate = { start: new Date(this.selectedDate), end: new Date(this.selectedDate) }
          selDate.start.setHours(9)
          selDate.start.setMinutes(0)
          selDate.end.setHours(10)
          selDate.end.setMinutes(0)
        }
        this.setSelectedEventDefaultData(false, selDate)
      },
      onEventCreate(event, deleteEventFunction) {
        if (this.filters.activeView !== 'week') {
          return;
        }
        event.class = 'hour-timeentry';
        if (event.source !== 'click') {
          this.deleteEventFunction = deleteEventFunction
          this.isDragging = true;
          return event;
        }
        if (this.isDragging) {
          return;
        }
        this.setSelectedEventDefaultData(event)

        this.deleteEventFunction = deleteEventFunction
        return event
      },
      onEventDragCreate(event) {
        this.setSelectedEventDefaultData(event)
      },
      monthCreateEvent (cell) {
        cell.startDate.setHours(9);
        cell.endDate = new Date(JSON.parse(JSON.stringify(cell.startDate)));
        cell.endDate.addHours(1);
        this.setSelectedEventDefaultData(false, { start: cell.startDate, end: cell.endDate })
      },
      setSelectedEventDefaultData (event = false, setDate = false) {
        this.selectedEvent = (event) ? event : {}
        this.selectedEvent.project = {}
        this.selectedEvent.project.id = this.$store.getters["filterStore/getCurrentProject"] || null
        this.selectedEvent.created_by = {}
        this.selectedEvent.created_by.id = this.currentUser.id;
        if(setDate) {
          this.selectedEvent.start = setDate.start;
          this.selectedEvent.end = setDate.end;
        }
        this.eventCreationState = 'create'
      },
      // deprecated
      onEventDragStart(e, draggable) {
        // Passing the event's data to Vue Cal through the DataTransfer object.
        e.dataTransfer.setData('event', JSON.stringify(draggable))
        e.dataTransfer.setData('cursor-grab-at', e.offsetY)
      },
      onDelete(event) {
        if (event.id && !this.updatingEvent) {
          HourService.destroy(event.id).then((response) => {
            if(response.status === 204)
              this.deleteHourFromCalendar(event);
          })
        }
      },
      onDeleteRecurrentHour(event) {
        this.recurrentDeleteResult = event;
        this.$refs.recurrent_hour_delete_confirmation_modal.open();
      },
      cancelEventCreation(action) {
        this.selectedEvent = null;
        this.isDragging = false;
        if (action === 'create' && this.deleteEventFunction) {
          this.deleteEventFunction();
        }
      },
      cancelConfirmationDialog() {
        this.recurrentUpdateResult = null;
        this.recurrentDeleteResult = null;
        this.selectedEvent = null;
        this.getHourList();
      },
      onSubmit(result) {
        this.isDragging = false;
        this.selectedEvent = null;
        if (result.action === 'create') {
          this.createHour(result.event);
        } else if (result.action === 'edit') {
          this.updateEvent(result.event);
        }
      },
      createHour(event) {
        var timeentry = HourService.eventToTimeentry(event, this.$store.getters["filterStore/getCurrentProject"]);

        HourService.create(timeentry).then((response) => {
          if (event.project && event.project.id)
            response.data.project = event.project;

          this.addNewHourToCalendar(response.data);
          this.$store.commit('setAlert', {title: this.$gettext("Hour Created"), text:this.$gettext("A new hour has been created"), variant:"success",duration:4});
        }).finally(() => {
          this.setWaitingForResponse(false)
        });
      },
      updateEvent (event) {
        if (this.updatingEvent) return false
        this.updatingEvent = true
        var timeentry = HourService.eventToTimeentry(event, this.$store.getters["filterStore/getCurrentProject"]);
        HourService.update(timeentry).then((response) => {
          this.updateHourFromCalendar(response.data);
          if (!event.cancel_update) {
            this.$store.commit('setAlert', {title: this.$gettext("Hour Edited"), text:this.$gettext("Your time entry has been edited"), variant:"success",duration:4});
          } else {
            this.$store.commit('setAlert', {title: this.$gettext("Edit not allowed"), text:this.$gettext("This entry is part of a string and cannot be moved to another day. Please create a new entry on this new day."), variant:"danger", duration:4});
          }
        })
        .catch((error) => {
          let errorTxt = this.$gettext('Something went wrong.');
          
          if (error.non_field_errors)
            errorTxt = error.non_field_errors; // the response should already be translated
          else if (error.data && error.data.created_by)
            errorTxt = this.$gettext('The user who created this entry is no longer connected to this company. Please select a new user to be assigned to this entry or reconnect the user by adding a team member.');
          
          this.$store.commit('setAlert', { 
            title: this.$gettext("Error"),
            text: errorTxt,
            variant: "danger",
            duration: 4 
          });
          
          this.updatingEvent = false
        }).finally(() => {
          this.setWaitingForResponse(false)
        });
      },
      onSubmitRecurrentHour(result) {
        this.isDragging = false;
        if (result.action === "create") {
          HourService.createRecurrentHour(result).then((response) => {
            let filters = { ...this.filters }
            
            response.data.time_entries.forEach((time_entry) => {
              if (result.template_time_entry.project) {
                time_entry.project = {id: result.template_time_entry.project};
              } else {
                time_entry.project = null;
              }

              let timeEntryDate = new Date(time_entry.date);
              let startDate = new Date(filters.start_date);
              let endDate = new Date(filters.end_date);
              
              if (timeEntryDate >= startDate && timeEntryDate < endDate) 
                this.addNewHourToCalendar(time_entry);
            });
            this.$store.commit('setAlert', {title: this.$gettext("Hours Created"), text:this.$gettext("New hours have been created"), variant:"success",duration:4});
            this.selectedEvent = null;
          }).finally(() => {
            this.setWaitingForResponse(false)
          });
        } else if (result.action === "edit") {
          this.recurrentUpdateResult = result;
          
          this.recurrentUpdateResult.selected_hour.newDate = result.selected_hour.date;
          this.recurrentUpdateResult.selected_hour.oldDate = this.selectedEvent.date;
          delete this.recurrentUpdateResult.selected_hour.date;

          this.$refs.recurrent_hour_update_confirmation_modal.open();
        }
      },
      onRecurrentHourUpdateConfirmation(result) {
        if (result === "one") {
          this.recurrentUpdateResult.selected_hour.date = this.recurrentUpdateResult.selected_hour.newDate;
          delete this.recurrentUpdateResult.selected_hour.newDate;
          delete this.recurrentUpdateResult.selected_hour.oldDate;
          
          HourService.update(this.recurrentUpdateResult.selected_hour).then((response) => {
            this.updateHourFromCalendar(response.data);
            this.$store.commit('setAlert', {title: this.$gettext("Hour Edited"), text:this.$gettext("Your time entry has been edited"), variant:"success",duration:4});
          }).finally(() => {
            this.setWaitingForResponse(false)
          });

          this.recurrentUpdateResult = null;
          this.selectedEvent = null;
        } else if (result === "all") {
          this.recurrentUpdateResult.selected_hour.date = this.recurrentUpdateResult.selected_hour.oldDate;
          delete this.recurrentUpdateResult.selected_hour.newDate;
          delete this.recurrentUpdateResult.selected_hour.oldDate;

          this.recurrentUpdateResult["selected_hour_date"] = this.recurrentUpdateResult.selected_hour.date;

          if(this.recurrentUpdateResult.template_time_entry.project && this.recurrentUpdateResult.template_time_entry.project.id) {
            this.recurrentUpdateResult.template_time_entry.project = this.recurrentUpdateResult.template_time_entry.project.id;
          }
          
          let data = JSON.parse(JSON.stringify(this.recurrentUpdateResult))
          if (typeof data.template_time_entry.created_by === 'object') {
            data.created_by = data.template_time_entry.created_by.id
            data.template_time_entry.created_by = data.template_time_entry.created_by.id
          }
          
          HourService.updateRecurrentHour(data).then((response) => {
            response.data.time_entries.forEach((time_entry) => {
              this.updateHourFromCalendar(time_entry);
            });
          
            this.$store.commit('setAlert', {title: this.$gettext("Hours Edited"), text:this.$gettext("The hours were edited"), variant:"success",duration:4});
            this.recurrentUpdateResult = null;
            this.selectedEvent = null;
          }).finally(() => {
            this.setWaitingForResponse(false)
          });
        }
      },
      onRecurrentHourDeleteConfirmation(result) {
        if (this.recurrentDeleteResult.id && !this.updatingEvent) {
          let params = {choice: result};
          HourService.destroyWithParams(this.recurrentDeleteResult.id, params).then((response) => {
            if(response.status === 204)
              this.deleteHourFromCalendar(this.recurrentDeleteResult);
          });
        }
      },
      addNewHourToCalendar(time_entry) {
        const newEvent = HourService.timeentry2Event(time_entry);
        newEvent.project = time_entry.project;
        newEvent.created_by = {};
        newEvent.created_by.id = time_entry.created_by;

        if(this.filters.activeView === "week"){
          this.events.push(newEvent);
        }else{
          this.$store.commit(this.parentMutation);
        }

        this.debouncedCreateRefresh(time_entry);
      },
      updateHourFromCalendar(time_entry) {
        for(var i=0;i<this.events.length; i++){
          if(this.events[i].id === time_entry.id){
            this.events.splice(i, 1);
            break;
          }
        }
        this.events.push(HourService.timeentry2Event(time_entry));

        this.debouncedEditRefresh(time_entry);
      },
      refreshOnCreate (time_entry) {
        // Mixpanel tracking
        if (window.mixpanel) {
          window.mixpanel.track("Hours: create", {
            "amount": time_entry.amount,
            "source": 'add hours page: timeline',
            "calendarView": this.filters.activeView,
            "has description": time_entry.description !== "",
            "hours for myself": time_entry.created_by
          });
        }
        this.refreshTotals();
      },
      refreshOnEdit (time_entry) {
        // Mixpanel tracking
        if (window.mixpanel) {
          window.mixpanel.track("Hours: edit", {
            "amount": time_entry.amount,
            "source": 'add hours page: timeline',
            "calendarView": this.filters.activeView,
            "has description": time_entry.description !== "",
            "hours for myself": time_entry.created_by
          });
        }

        this.$store.commit(this.parentMutation);
        this.refreshTotals();
        this.updatingEvent = false;
      },
      deleteHourFromCalendar(time_entry) {
        // Mixpanel tracking
        if (window.mixpanel) {
          window.mixpanel.track("Hours: delete", {
            "amount": time_entry.amount,
            "source": 'add hours page: timeline',
            "calendarView": this.filters.activeView,
            "has description": time_entry.description !== "",
            "hours for myself": time_entry.created_by.id
          })
        }
        
        if (time_entry.recurrent_time_entry)
          this.$store.commit('setAlert', {title: this.$gettext("Hours Deleted"), text:this.$gettext("The hours were deleted"), variant:"danger",duration:4});
        else
          this.$store.commit('setAlert', {title: this.$gettext("Hour Deleted"), text:this.$gettext("Deleted an hour"), variant:"danger",duration:4});
        
        this.$store.commit(this.parentMutation);
        this.refreshTotals();
      },
      selectProjectCustomer (payload) {
        this.$emit('selected-project', payload.project)
        if(!payload.firstTime) {
          this.scrollTrigger = 'sidemenu-select'
          let project_id, customer_id
          if(payload.unassigned) {
            project_id = null
            customer_id = null
            this.$store.commit("filterStore/SET_UNASSIGNED", true);
          } else {
            project_id = (payload.project === null) ? null : payload.project.id
            customer_id = (payload.project === null) ? null : payload.project.company
            this.$store.commit("filterStore/SET_UNASSIGNED", false);
          }
          this.filters.project = project_id;
          this.filters.customer = customer_id;
          this.$store.commit("filterStore/SET_CUSTOMER_PROJECT", { customer: customer_id, project: project_id });

          if(payload.list !== 'customers') {
            this.$store.commit("filterStore/setCurrentCustomer", null);
            this.filters.customer = null;
          } else {
            this.$store.commit("filterStore/setCurrentCustomer", customer_id);
            this.filters.customer = customer_id;
          }
          this.$store.commit(this.parentMutation);
          this.refreshTotals(null, 'HourCustomerSidemenu')
        }
      },
      selectCustomer (payload) {
        this.$emit('selected-project', { id: false, company: payload.customer.id})
        this.$store.commit("filterStore/SET_UNASSIGNED", false);
        if(!payload.firstTime) {
          this.scrollTrigger = 'sidemenu-select'
          const project_id = null
          const customer_id = (payload.customer === null) ? null : payload.customer.id
          this.filters.project = project_id;
          this.filters.customer = customer_id;
          this.$store.commit("filterStore/SET_CUSTOMER_PROJECT", { customer: customer_id, project: project_id });

          this.$store.commit(this.parentMutation);
          this.refreshTotals(null, 'HourCustomerSidemenu')
        }
      },
      getTotalEventTime (start, end) {
        let diffInMilliSeconds = Math.abs(new Date(start) - new Date(end)) / 1000;
        // days
        const days = Math.floor(diffInMilliSeconds / 86400);
        diffInMilliSeconds -= days * 86400;
        // hours
        const hours = Math.floor(diffInMilliSeconds / 3600) % 24;
        diffInMilliSeconds -= hours * 3600;
        // minutes
        const minutes = Math.ceil(diffInMilliSeconds / 60) % 60;
        diffInMilliSeconds -= minutes * 60;
        let difference = '';
        if (days > 0) {
          difference += (days === 1) ? `${days}d ` : `${days}d `;
        }
        difference += (hours === 0 || hours === 1) ? `${hours}h ` : `${hours}h `;
        difference += (minutes === 0 || hours === 1) ? `${minutes}m` : `${minutes}m`; 
        return difference;
      },
      getHoursWeekday (events, weekday) {
        var eventsDuration = 0
        events.forEach(event => {
          if (this.datesSameDay(new Date(weekday), new Date(event.start))) {
            if(event.amount_hours || event.amount_minutes) {
              eventsDuration += (event.amount_hours * 60) + event.amount_minutes
            }
          }
        });
        return this.minutesToHours(eventsDuration)
      },
      getHeadingWeekday (heading) {
        return moment(heading.date).format("ddd D");
      },
      datesSameDay (first, second) {
        return first.getFullYear() === second.getFullYear() &&
        first.getMonth() === second.getMonth() &&
        first.getDate() === second.getDate();
      },
      minutesToHours(n) {
        var num = n;
        var hours = (num / 60);
        var rhours = Math.floor(hours);
        var minutes = (hours - rhours) * 60;
        var rminutes = Math.round(minutes);
        return rhours + "h " + rminutes + "m";
      },
      getDayHours (time) {
        return time.hours + 'h ' + ((time.minutes < 10) ? '0' : '') + time.minutes + 'm'
      },
      scrollToHour () {
        // Scroll only when navigating (not on event add/update)
        // If trigger is view change add delay to let weekly view to fully render
        let scrollDelay = this.$store.getters['hourStore/getScrollDelay']
        this.$store.commit("hourStore/SET_SCROLL_DELAY", 0);
        setTimeout(() => {
          if(this.$store.getters['hourStore/getDoScroll'] || this.scrollTrigger === 'sidemenu-select') {
            let minHour = (this.events.length === 0) ? this.gotoDefault : Math.min(...this.events.map(({ start }) => new Date(start.replace(/-/g, "/")).format('H') ))
            const calendar = document.querySelector('#vuecal .vuecal__bg')
            if (calendar) {
              calendar.scrollTo({ top: minHour * this.timeCellHeight, behavior: 'smooth' });
              this.$store.commit("hourStore/SET_DO_SCROLL", false);
              this.scrollTrigger = ''
            }
          }
        }, scrollDelay);
      },
      refreshTotals (targetComponent = null, excludeComponent = null) {
        this.$store.commit(this.childMutation, { refresh: targetComponent, exclude: excludeComponent });
      },
      hourListUpdated (time_entries) {
        this.getHoursPerDay();
        this.refreshTotals();

        time_entries.forEach((time_entry_id) => {
          let ev = this.events.find((e) => e.id === time_entry_id);
          
          ev.is_invoiced = true;
          ev.invoiced = true;

          this.updateHourFromCalendar(ev);
        });
      },
      onOpenContactModal () {
        this.openContactModal = true
      },
      onOpenProjectModalMini () {
        this.openProjectModalSimple = true
      },
      onOpenProjectModal (contact_id = null) {
        this.openProjectModal = true
        if(contact_id) {
          this.projectModalContactId = contact_id
        }
      },
      onProjectCreated (val) {
        this.filters.project = val.id;
        this.filters.customer = val.company;
        this.$store.commit("filterStore/SET_CUSTOMER_PROJECT", { customer: val.company, project: val.id });
        this.resetModals()
        this.refreshTotals('HourCustomerSidemenu', null)
        this.$store.commit('filterStore/SET_COOKIES')
      },
      onContactCreated (val) {
        this.$store.commit("filterStore/setCurrentCustomer", val.id);
        this.filters.customer = val.id;
        this.resetModals()
        this.refreshTotals('HourCustomerSidemenu', null)
        this.$store.commit('filterStore/SET_COOKIES')
      },
      onModalCancel () {
        this.resetModals()
      },
      resetModals () {
        this.openProjectModal = false
        this.openContactModal = false
        this.openProjectModalSimple = false
        this.projectModalContactId = null
      },
      isTouchDevice() {
        return (('ontouchstart' in window) ||
          (navigator.maxTouchPoints > 0) ||
          (navigator.msMaxTouchPoints > 0));
      },
      getAmountColor (hour) {
        let text_color = 'text-uninvoiced'
        if (hour.amount === hour.invoiced_total || this.filters.status === 'invoiced') {
          text_color = 'text-invoiced'
        }else if (hour.amount === hour.unassigned_total) {
          text_color = 'text-unassigned'
        }
        return text_color
      },
      openCopyModal(event) {
        if (typeof event.start === "string")
          event.start = Date.parse(event.start);

        this.hourToCopy = event;
        this.$refs.copy_hour_modal.showModal = true;
      },
      onCopyHour(newHour) {
        this.createHour(newHour);
      }
    }
  };
</script>

<style lang="scss" scoped>
  .hours-table-wrap {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .hours-wrapper {
    display: flex; 
    flex: 1;
    overflow: initial !important;

    border: none !important;
    border-radius: 0px !important;
    gap: 1rem;
    padding-top: 0.5rem;
  }
  @media(max-width: '767px') {
    .hours-wrapper {
      flex-direction: column;
      .calendar-sidemenu {
        max-width: none;
        // padding-right: 0rem !important;
        margin-top: 0rem;
        margin-bottom: 1rem;
      }
    }
  }

  .calendar-sidemenu {
    min-width: 250px;
    // padding: 0.5rem;
    // background: $colorBgSidebar;
  }
  .calendar-wrapper {
    overflow: auto;
    // padding: 15px;
    position: relative;
    width: 100%;
    // background: white;
    .calendar {
      position: relative;
      aspect-ratio: 16/10;
      @supports not (aspect-ratio: 16/10) {
        width: 100%;
        height: 50vw;
        min-height: 500px;
        max-height: 700px;
      }
      transition: opacity 0.2s ease;
      &.loading { opacity: 0.3; }
      &.calendar-month {
        aspect-ratio: auto;
        width: 100%;
      }
      &.hide { 
        display: none;
      }
    }
  }
</style>

<style lang="scss">
  $borderTable: 1px solid #E2E2E2;
  $borderCell: 1px solid #F2EFEF;
  $timeColumnWidth: 1.5rem;
  $colorEvenOddBg: rgb(248, 248, 248, 0.5); // #F8F8F8;
  .calendar {
    margin-bottom: 1rem;
    &.shadowed {
      border-radius: $radiusMd;
      overflow: hidden;
      .vuecal__weekdays-headings {
        background: $tableHeadColorBg;

        border: 1px solid $colorBorderDash;
        border-top-left-radius: $radiusLg;
        border-top-right-radius: $radiusLg;
      }
      .vuecal__body {
        border: 0;
        border-top: 1px solid #e5e7eb;
        border-radius: 0px 0px $radiusLg $radiusLg;
        margin-bottom: -2px;
      }
    }
  }
  .vuecal {
    box-shadow: none;
  }
  .vuecal__body {
    border: $borderTable;
    border-radius: $radiusLg;
  }
  .vuecal__bg {
    padding-top: 10px;
  }
  .vuecal__heading {
    height: 35px;
    line-height: 1rem;
    font-size: .75rem;
    color: #6b7280;
    .weekday-heading {
      display: flex;
      justify-content: space-between;
      width: 100%;
      align-items: center;
      padding: 0px 10px;
      line-height: 1;
      .label { }
      .time { color: $colorTextBase; }
    }
  }
  .vuecal--month-view .vuecal__cell { height: 50px; }
  .vuecal--month-view .vuecal__cell-content { justify-content: end; }
  .vuecal--month-view {
    .vuecal__body { border: none; border-radius: 0; }
    .vuecal__bg { padding: 0; }
    .vuecal__weekdays-headings { padding-right: 0; }
  }
  .vuecal__weekdays-headings {
    border-bottom: none;
  }

  .vuecal--view-with-time .vuecal__weekdays-headings {
    padding-left: $timeColumnWidth;
    padding-right: $timeColumnWidth;
  }
  
  .vuecal__time-column {
    width: $timeColumnWidth;
    .vuecal__time-cell{
      display: flex;
      .vuecal__time-cell-line {
        &:before {
          left: $timeColumnWidth;
        }
      }
      .vuecal__time-cell-label {
        font-size: 10px;
        color: #929292;
        margin-top: -6px;
        display: block;
        width: 100%; 
        text-align: center;
      }
    }
  }
  
  .vuecal__now-line { color: cyan; }

  .vuecal__event.hour-timeentry {
    border: 1px solid #d3c6c6;
    color: #fff;
    background-color: transparent;
    border: 0;
    cursor: pointer;
    min-height: 26px;
    padding: 1px;
    &.timeentry-invoiced { 
      .timeentry-content { 
        background: $colorInvoiced;
        .vuecal__event-time { background: #7caf4b; } 
      } 
    }
    &.timeentry-uninvoiced { 
      .timeentry-content { 
        background: $colorUninvoiced;
        .vuecal__event-time { background: #9d5d70; } 
      } 
    }
    &.timeentry-unassigned { 
      .timeentry-content { 
        background: #ddd;
        .vuecal__event-description, .vuecal__event-name { color: $colorUnassigned; }
        .vuecal__event-time { background: $colorUnassigned; } 
      } 
    }
    .timeentry-content {
      height: 100%;
      overflow: hidden;
      text-align: left;
      position: relative;
      background-color: $colorBlue;
      .vuecal__event-time {
        pointer-events: none;
        background: $colorBlue;
        font-size: 12px; 
        position: absolute;
        z-index: 10;
        bottom: 0px;
        right: 0px;
        padding: 3px;
        padding-bottom: 0px;
        border-radius: $radiusLg 0px $radiusLg 0px;
      }
      border-radius: $radiusLg;
      padding: 5px;
    }
    .vuecal__event-resize-handle {
      background: #eff1f2;
      height: 13px;
      border-radius: 0px 0px $radiusLg $radiusLg;
      &:before,
      &:after {
        content: '';
        position: absolute;
        left: 50%;
        transform: translate(-50%, -50%);
        height: 0.1px;
        width: 25px;
        background: #9b9b9b;
      }
      &:before { top: 40%; }
      &:after { top: 60%; }
    }
  }

  .week-view {
    .vuecal__cell {
      padding: 0px 7px;
      &:before { border: 1px solid #eeeeee; }
      &--selected { 
        background-color: transparent;
      }
      &:nth-child(6n), &:nth-child(7n) { 
        &:not(.vuecal__cell--current):not(.vuecal__cell--today) {
          background: #d9d9d929;
        }
      }
      &--current { background-color: rgba(0, 0, 66, 0.5); }
      &--today { background-color: #{$colorBlue}12; } // 0.07% alpha
    }
  }
  .month-view {
    .vuecal__cell {
      &:nth-child(7n - 1),
      &:nth-child(7n) { 
        &:not(.vuecal__cell--current):not(.vuecal__cell--today) {
          background: $colorEvenOddBg;
        }
      }
      
      &:before {
          transition: border 0.2s ease;
        }
      &--selected { 
        background-color: transparent;
      }
      &--today { background-color: #{$colorBlue}12; } // 0.07% alpha
      .focused {
        height: 100%;
        border-bottom: 2px solid $colorBrand;
        background-color: rgba(235,255,245,.4);
        margin-bottom: -1px;
      }
    }
  }
  
  .vuecal__event-description { font-size: 14px; line-height: 1.2; }
  .vuecal__event-name { font-size: 12px; opacity: 0.7; line-height: 1.2; }
  .vuecal:not(.vuecal--dragging-event) .vuecal__event:hover {
    .vuecal__event-delete{
      transform: translateY(0);
      color: #fff;
    };
    z-index: 2;
  }

  .month-cell-overlay {
    height: 100%;
    width: 100%;
    cursor: pointer;
    position: absolute; top: 0; left: 0;
    outline: none;
    .month-actions {
      position: absolute;
      display: none;
      z-index: 2;
      color: $colorBrand;
      font-size: 26px;
      height: auto;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      background: white;
      border-radius: 50px;
      .svg-inline--fa { line-height: 24px; }
    }
    &.visible {
      .month-actions {
        display: flex;
      }
    }
  }
  .vuecal__cell-events-count.gk-count {
    font-size: 15px;
    background: none;
    pointer-events: none;
    transform: none; top: auto; left: auto; padding: 0; border-radius: 0;
    display: block;
    line-height: 1;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    text-align: center;
  }

  @media(min-width: '768px') {
    .vuecal__cell-events-count.gk-count{
      font-size: 17px;
    }
  }

  .vuecal__cell {
    .vuecal__cell-date {
      position: absolute;
      top: 5px;
      left: 5px;
      font-size: 13px;
    }
    &:not(.vuecal__cell--out-of-scope) { color: #a7a7a7; }
    &.vuecal__cell--out-of-scope { color: rgba(0,0,0,0.2); }
    &.vuecal__cell--today { color: $colorTextBase; }
  }
  .vuecal__no-event { display: none; }

  .circle-wrap {
    position: absolute;
    opacity: 0; 
    pointer-events: none;
    &.visible { opacity: 0.5; }
    font-size: 10px;
    line-height: 10px;
    display: flex;
    align-items: center;
    .hint {
      width: 75px;
    }
  }
  .circle {
    border: solid 1px $colorBrand;
    border-radius: 50px;
    width: 17px; 
    height: 17px; 
    pointer-events: none;
    margin-right: 5px;
    position: relative;
    &:before, &:after {
      content: '';
      background: $colorBrand;
      position: absolute;
    }
    &:before { height: 9px; width: 1px; top: 3px; left: 7px; }
    &:after { width: 9px; height: 1px; top: 7px; left: 3px; }
  }
  .vuecal__event--multiple-days {
    &.event-end { 
      opacity: 0.3;
      .vuecal__event-time { display: none; }
    }
  }
  .vuecal__event-avatar {
    position: absolute;
    bottom: 0;
    opacity: 0.5;
    height: 25px;
    width: 25px;
    .user-display-name {
      font-size: 12px;
      opacity: 0.7;
      line-height: 1;
    }
    .profile-badge {
      margin-right: 5px;
      margin-bottom: 3px;
      $avatarSize: 20px;
      .avatar { 
        margin: 0; 
        min-width: $avatarSize;
        min-height: $avatarSize;
        width: $avatarSize;
        height: $avatarSize;
      }
      .no-avatar {
        font-size: 12px;
        color: #666;
        width: $avatarSize;
        height: $avatarSize;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #ddd;
        border-radius: 20px;
        padding-top: 2px;
      }
    }
  }

  .timeentry-copy-btn {
    opacity: 0;
    position: absolute;
    bottom: 25px;
    right: 3px;
    background: none;
    border: 0;
    outline: 0;
    color: white;
    svg { width: 20px; }
    padding: 3px;
    &:hover {
      .fa {
        opacity: 0.7;
      }
    }
  }

  .timeentry-content {
    &:hover {
      .timeentry-copy-btn {
        opacity: 1;
      }
    }
  }

  .timeentry-unassigned {
    .timeentry-content {
      .timeentry-copy-btn {
        color: $colorUnassigned;
      }
    }
  }

  .vuecal__event--static {
    opacity: 50%;
  }

  #copy-button-icon > path {
    pointer-events: none;
  }
</style>
